/* Changes from several devices are merged by the database in the order they were really made (migration 016, owner
   request 2026-10-30). This is the pure part on the device (no network, no DOM; tests/app/merge.test.ts):
   - madeAt: when a queued change was really made (the tap). Changes queued by an older app version have none: a timer
     start counts as made when the timer started, a stop when it ended (both are the real tap times); anything else
     counts as made when it reaches the server (said so in the code that sends it, db.ts).
   - the database's notes about what it merged, in plain words ("Kenny Deck ended 3:24:59 PM because Buttercup was started
     at 3:25 PM here."). The person is told, never asked. */
import type {Op} from './sync.ts';

/** one thing the database did while merging (see migrations/016: the "notes" of the op_* functions) */
export interface MergeItem { d: string; p: string; s?: string; e?: string }
export interface MergeNote {
  k: 'cut' | 'before' | 'untilNext' | 'coverCut' | 'moved' | 'covered' | 'duplicate' | 'stopKept' | 'stopMoved' | 'stopFitted' | 'stopChanged'
    | 'staleEdit' | 'staleDelete' | 'deletedLater' | 'readded' | 'staleTimer' | 'onSaved' | string;
  a?: MergeItem | null; b?: MergeItem | null;
  at?: string; end?: string; start?: string; from?: string; until?: string; mine?: string; made?: string; changed?: string;
}

/** when the change was really made (ISO), or undefined: "when it reaches the server" */
export function opMadeAt(op: Op): string | undefined {
  if (op.madeAt) return op.madeAt;
  if (op.kind === 'startTimer') return op.timer.startedAt;
  if (op.kind === 'stopTimer') {   // the stop's end instant: its start (wall clock + offset) + duration
    const e = op.entry, ms = Date.parse(e.startedAt + 'Z') - e.so * 60000 + e.durationSeconds * 1000;
    return isFinite(ms) ? new Date(ms).toISOString() : undefined;
  }
  return undefined;
}

export interface NoteFmt {
  /** "3:24:59 PM" (seconds only when not :00), in the person's zone; a day other than today gets its date */
  clock: (iso: string) => string;
  /** a project's name (empty if unknown) */
  project: (id: string) => string;
}
/** the name of what was merged: its description, else its project, else "A timer" */
export function itemName(it: MergeItem | null | undefined, f: NoteFmt): string {
  if (!it) return 'the other timer';
  const n = (it.d || '').trim() || f.project(it.p) || '';
  return n ? '“' + n + '”' : 'a timer without a description';
}
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
/** one note in plain words */
export function noteText(n: MergeNote, f: NoteFmt): string {
  const a = itemName(n.a, f), b = itemName(n.b, f), t = (x?: string) => x ? f.clock(x) : '?';
  const span = (it?: MergeItem | null) => it?.s && it?.e ? `${t(it.s)}–${t(it.e)}` : '';
  switch (n.k) {
    case 'cut': return `${cap(a)} ended ${t(n.end)} because ${b} was started at ${t(n.at)} here.`;
    case 'before': return `${cap(b)} was saved ${t(n.start)}–${t(n.end)}: ${a} was started at ${t(n.at)} on another device and keeps running.`;
    case 'untilNext': return `${cap(b)} was saved ${t(n.start)}–${t(n.end)}: the next timer was started at ${t(n.at)} on another device.`;
    case 'coverCut': return `${cap(a)} ended ${t(n.end)} because ${b} was started at ${t(n.at)} here; ${b} ran until ${t(n.until)}, when the timer was stopped on another device.`;
    case 'moved': return `${cap(b)} starts ${t(n.at)} instead of ${t(n.from)}, right after ${a} (saved on another device), so the two do not overlap.`;
    case 'covered': return `${cap(b)} (started ${t(n.at)}) was already covered by time saved on another device, so nothing was added twice.`;
    case 'duplicate': return `${cap(b)} (started ${t(n.at)}) was already saved on another device${span(n.a) ? ' (' + span(n.a) + ')' : ''}, so it was not counted again.`;
    case 'stopKept': return `${cap(a)} had already been stopped on another device: it keeps ending at ${t(n.end)}${n.mine ? ` (the stop here, ${t(n.mine)}, would have overlapped what came next or was made earlier)` : ''}.`;
    case 'stopMoved': return `${cap(a)} now ends ${t(n.end)} instead of ${t(n.from)}: the stop here was made after the one on another device.`;
    case 'stopFitted': return `${cap(a)} was saved ${span(n.a)} so it does not overlap time saved on another device.`;
    case 'stopChanged': return `${cap(a)} was saved ${span(n.a)} with the changes made to it on another device.`;
    case 'staleEdit': return `Your change to ${a} was not saved: it was changed on another device later (${t(n.changed)}). The newer version is shown.`;
    case 'staleDelete': return `${cap(a)} was not deleted: it was changed on another device after you deleted it here.`;
    case 'deletedLater': return `Your change to ${a} was not saved: it was deleted on another device afterwards.`;
    case 'readded': return `${cap(a)} had been deleted on another device earlier; your later change here put it back.`;
    case 'staleTimer': return `Your change to the running timer ${a} was not saved: it was changed on another device later (${t(n.changed)}).`;
    case 'onSaved': return `The timer had already been stopped on another device, so your change went onto its saved entry ${a}${span(n.a) ? ' (' + span(n.a) + ')' : ''}.`;
    default: return 'Merged with a change from another device.';
  }
}
